/**
 * useRollupData Hook (Story 40-6 — Epic 40).
 *
 * The roll-up sibling of `useReportData`: fetches live, cross-client aggregated
 * widget data via POST /api/reports/populate-rollup with a MEMBER SET
 * (`memberClientIds[]`) instead of a single `clientId`. Returns the SAME
 * `UseReportDataResult` contract so the canvas + preview consume it identically
 * — the only difference is the data PATH (the 40-4 fan-out engine).
 *
 * Parity with useReportData is deliberate and load-bearing: the widget
 * signature, staleTime/gcTime derivation, keepPreviousData, and the
 * envelope-building are mirrored 1:1 (shared helpers imported, not re-derived) so
 * the two surfaces behave identically. The query key is prefixed "rollup-data"
 * and carries the SORTED member set, so two roll-ups over the same range but
 * different members never collide (mirrors the server cache-key discipline).
 */

import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { apiPost } from "../lib/apiClient";
import type { WidgetConfig } from "../lib/templateUtils";
import type { WidgetData, AnyWidgetPayload } from "../lib/widgetData";
import { createLoadingEnvelope, createErrorEnvelope } from "../lib/widgetData";
import { deriveStaleTimeMs } from "../lib/reportStaleTime";
import {
  stableStringify,
  type PopulateResponse,
  type UseReportDataResult,
} from "./useReportData";

export function useRollupData(
  memberClientIds: string[],
  widgets: WidgetConfig[],
  dateRange: { start: string; end: string },
  comparison?: { start: string; end: string } | null,
  options?: { forceRefresh?: boolean }
): UseReportDataResult {
  const queryClient = useQueryClient();

  const comparisonKey = comparison
    ? `${comparison.start}_${comparison.end}`
    : "none";

  // The aggregate response depends on the MEMBER SET — fold a stable, SORTED
  // member signature into the key so adding/removing a member re-keys (and never
  // serves another member set's cached blob under the raised staleTime).
  const memberKey = useMemo(
    () => [...memberClientIds].sort().join(","),
    [memberClientIds]
  );

  // Identical to useReportData (review R1/R2): the populate response depends on
  // each widget's id + type + config; title/position are excluded; SORTED + a
  // throw-safe stable stringify so a cosmetic re-key / a non-serializable config
  // never spuriously refetches or crashes the editor.
  const widgetsSignature = useMemo(
    () =>
      widgets
        .map((w) => stableStringify({ id: w.id, type: w.type, config: w.config }))
        .sort()
        .join("|"),
    [widgets]
  );

  const queryKey = useMemo(
    () => [
      "rollup-data",
      memberKey,
      dateRange.start,
      dateRange.end,
      comparisonKey,
      widgetsSignature,
    ],
    [memberKey, dateRange.start, dateRange.end, comparisonKey, widgetsSignature]
  );

  const staleTime = useMemo(() => deriveStaleTimeMs(widgets), [widgets]);
  const gcTime = staleTime;

  const { data, isLoading, isError, error, isFetching, isPlaceholderData } =
    useQuery<PopulateResponse>({
      queryKey,
      placeholderData: keepPreviousData,
      staleTime,
      gcTime,
      queryFn: async () => {
        const body: Record<string, unknown> = {
          memberClientIds,
          widgets: widgets.map((w) => ({
            id: w.id,
            type: w.type,
            title: w.title,
            position: w.position,
            config: w.config,
            ...(w.autoSize !== undefined ? { autoSize: w.autoSize } : {}),
          })),
          dateRange: { start: dateRange.start, end: dateRange.end },
        };
        if (comparison) {
          body.comparison = { start: comparison.start, end: comparison.end };
        }
        if (options?.forceRefresh) {
          body.forceRefresh = true;
        }

        const res = await apiPost("/api/reports/populate-rollup", body);
        if (!res.ok) {
          const text = await res.text().catch(() => "Unknown error");
          throw new Error(
            `Failed to populate roll-up data (${res.status}): ${text}`
          );
        }
        const json = await res.json();
        return json.data as PopulateResponse;
      },
      // No member set ⇒ no call (loading/empty, never a wasted populate or a crash).
      enabled:
        memberClientIds.length > 0 &&
        widgets.length > 0 &&
        !!dateRange.start &&
        !!dateRange.end,
    });

  const widgetData = useMemo(() => {
    if (isError) {
      const errData: Record<string, WidgetData> = {};
      for (const widget of widgets) {
        errData[widget.id] = createErrorEnvelope<AnyWidgetPayload>(
          error?.message ?? "Failed to load roll-up data"
        );
      }
      return errData;
    }
    if (isLoading || !data) {
      const loading: Record<string, WidgetData> = {};
      for (const widget of widgets) {
        loading[widget.id] = createLoadingEnvelope<AnyWidgetPayload>();
      }
      return loading;
    }
    return data.widgetData;
  }, [data, isLoading, isError, error, widgets]);

  const platformStatus = data?.platformStatus ?? {};

  const hasStaleComparison = useMemo(() => {
    if (!comparison) return false;
    return Object.values(platformStatus).some(
      (ps) => ps.status === "success" && ps.stale
    );
  }, [platformStatus, comparison]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const isUpdating = isFetching && isPlaceholderData;

  return {
    widgetData,
    platformStatus,
    isLoading,
    isFetching,
    isPlaceholderData,
    isUpdating,
    isError,
    error: error as Error | null,
    hasStaleComparison,
    refetch,
  };
}
